import React, { useState, useEffect, useMemo } from 'react';
import { Formik, ErrorMessage } from 'formik';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import { CSSTransition } from 'react-transition-group';
import './styles.css';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import CheckboxMultiSelect from '../../../../components/CheckboxMultiSelect';
import CheckboxOrRadioGroup from '../../../../components/CheckboxOrRadioGroup';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LanguageSelectGroup from '../../../../components/LanguageSelectGroup';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import useApi from '../../../../hooks/useApi';
import {ProfileEditPersonalInfoFormValidationSchema} from './validate';

const ProfileEditPersonalInfoForm = props => {
  const {
    actions: { handleError, proceed, dismiss },
    params,
  } = props;

  const api = useApi();

  const [state, setState] = useState({
    init: false,
    fields: {
      Languages: null,
      ImmediateStartOpportunities: null,
      ManagePersonalAccount: null,
    },
  });

  const [endpointGet, endpointSave] = useMemo(() => {
    const selfEdit = !params || !params.id;
    return selfEdit
      ? [
          ENDPOINT.FORM.GET.PERSONAL_INFORMATION,
          ENDPOINT.FORM.SAVE.PERSONAL_INFORMATION,
        ]
      : [
          ENDPOINT.FORM.GET.EDIT_CANDIDATE_PROFILE_PERSONAL_INFORMATION,
          ENDPOINT.FORM.SAVE.EDIT_CANDIDATE_PROFILE_PERSONAL_INFORMATION,
        ];
  }, []);

  useEffect(() => {
    api.get(
      endpointGet,
      { id: params.id },
      {
        handleError: handleError,
        handleSuccess: data => {
          const fields = {
            ...data.fields,
            CitizenshipID: [],
            Languages: data.fields.Languages
              ? data.fields.Languages.map(value => {
                  return {
                    language: data.options.Languages.find(
                      language => language.value === value.language
                    ),
                    level: data.options.Levels.find(
                      level => level.value === value.level
                    ),
                  };
                })
              : [],
          };

          const options = {
            ...data.options,
            CitizenshipID: data.options.CitizenshipID.map(item => {
              const obj = {
                ...item,
                id: item.value,
              };
              if (
                data.fields.CitizenshipID &&
                data.fields.CitizenshipID.length &&
                data.fields.CitizenshipID.indexOf(item.value) !== -1
              ) {
                fields.CitizenshipID.push(obj);
              }

              return obj;
            }),
          };

          setState({
            init: true,
            fields: {
              ...state.fields,
              ...fields,
            },
            options,
          });
        },
      }
    );
  }, []);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const preparedValues = clone(values);

    if (params && params.id) {
      preparedValues.id = params.id;
    }

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null) {
        delete preparedValues[field];
      } else if (field === 'Languages') {
        preparedValues[field] = data.map(value =>
          JSON.stringify({
            language: value.language.value,
            level: value.level.value,
          })
        );
      } else if (isArray(data)) {
        preparedValues[field] = data.map(item => item.id);
      } else if (typeof data === 'object') {
        preparedValues[field] = data.value;
      }
    });

    api.post(endpointSave, preparedValues, {
      handleForbidden: handleError,
      handleError: response => {
        handleError(response, setErrors);
        setSubmitting(false);
      },
      handleSuccess: () => proceed(values),
    });
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={state.fields}
          validationSchema={ProfileEditPersonalInfoFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}
              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <div className="form__row">
                  <BasicLabel
                    name="ManagePersonalAccount"
                    required={true}
                    text="I manage my personal account"
                    styles={{
                      fontWeight: 'bold',
                    }}
                  />
                </div>
                <div className="form__row">
                  <CheckboxOrRadioGroup
                    name="ManagePersonalAccount"
                    type="radio"
                    options={['Yes', 'No']}
                    selectedOptions={values.ManagePersonalAccount}
                    directionRow={true}
                    controlFunc={handleChange}
                  />
                </div>
                <ErrorMessage
                  name="ManagePersonalAccount"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <div className="form__row">
                  <BasicLabel
                    name="ImmediateStartOpportunities"
                    required={true}
                    text="I am open to immediate start opportunities"
                    styles={{
                      fontWeight: 'bold',
                    }}
                  />
                </div>
                <div className="form__row">
                  <CheckboxOrRadioGroup
                    name="ImmediateStartOpportunities"
                    type="radio"
                    options={['Yes', 'No']}
                    selectedOptions={values.ImmediateStartOpportunities}
                    directionRow={true}
                    controlFunc={handleChange}
                  />
                </div>
                <ErrorMessage
                  name="ImmediateStartOpportunities"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '25px' }}
              >
                <CheckboxMultiSelect
                  name="CitizenshipID"
                  label="Citizenship"
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                  isLast={true}
                  isValid={
                    (!errors.CitizenshipID && !!touched.CitizenshipID) ||
                    !touched.CitizenshipID
                  }
                  required={true}
                  selectedItems={values.CitizenshipID}
                  showSearch={true}
                  items={state.options.CitizenshipID.filter(i => i.label !== 'N/A')}
                  placeholder=""
                  groups={false}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
                <ErrorMessage
                  name="CitizenshipID"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '25px' }}
              >
                <div className="form__row">
                  <BasicLabel
                    name="Languages"
                    required={true}
                    text="Foreign Languages"
                    styles={{
                      fontWeight: 'bold',
                    }}
                  />
                </div>
                <LanguageSelectGroup
                  name="Languages"
                  values={values.Languages}
                  isValid={
                    (!errors.Languages && !!touched.Languages) ||
                    !touched.Languages
                  }
                  required={false}
                  levelOptions={state.options.Levels}
                  languageOptions={state.options.Languages}
                  onChange={setFieldValue}
                />
                <ErrorMessage
                  name="Languages"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Update"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default ProfileEditPersonalInfoForm;
